import React, { useEffect, useState } from 'react';
import SystemDashboard from 'layouts/SystemDashboard';
import {
  Alert,
  Box,
  Breadcrumbs,
  Grid,
  TextField,
  Button,
  Typography,
  Link,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { extractErrorMessage } from 'utils/extractErrorMessage';
import { useNavigate, useParams } from 'react-router-dom';
import Container from 'components/Container';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
  getSubscription,
  saveSubscriptionByMembership,
  updateSubscription,
} from './SubscriptionService';
import SubscriptionProductList from './SubscriptionProductList';
import SubscriptionGroupList from './SubscriptionGroupList';
import PaymentList from '../Payments/PaymentList';

const SubscriptionDetail = () => {
  const navigate = useNavigate();
  const { membershipid, subscriptionid } = useParams();
  const [, setIsSaved] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const validationSchema = yup.object({
    subscriptionName: yup.string().required('Subscription Name is required.'),
    startDate: yup.date().nullable().required('Start Date is required.'),
    endDate: yup
      .date()
      .nullable()
      .min(yup.ref('startDate'), 'End Date cannot be before Start Date')
      .required('End Date is required.'),
    status: yup.string().required('Status is required.'),
    paymentFrequency: yup.string().required('Payment Frequency is required.'),
    nextPaymentDate: yup
      .date()
      .nullable()
      .required('Next Payment Date is required.'),
    trial: yup.boolean(),
    autoRenew: yup.boolean(),
    expired: yup.boolean(),
    storageExceeded: yup.boolean(),
    usersExceeded: yup.boolean(),
    trialComplete: yup.boolean(),
    enabled: yup.boolean(),
  });

  const formik = useFormik({
    initialValues: {
      subscriptionName: '',
      startDate: '',
      endDate: '',
      status: '',
      paymentFrequency: '',
      nextPaymentDate: '',
      trial: false,
      autoRenew: false,
      expired: false,
      storageExceeded: false,
      usersExceeded: false,
      trialComplete: false,
      enabled: false,
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const subscription = {
        ...values,
        startDate: values.startDate
          ? new Date(values.startDate).toISOString()
          : null,
        endDate: values.endDate ? new Date(values.endDate).toISOString() : null,
        nextPaymentDate: values.nextPaymentDate
          ? new Date(values.nextPaymentDate).toISOString()
          : null,
      };

      if (subscriptionid) {
        updateSubscription(subscriptionid, subscription)
          .then(() => {
            setIsSaved(true);
          })
          .catch((error) => setErrorMessage(extractErrorMessage(error)));
      } else {
        saveSubscriptionByMembership(membershipid, subscription)
          .then((response) => {
            const newSubscriptionId = response.data.id;
            setIsSaved(true);
            navigate(
              `/system/dashboard/membership/${membershipid}/subscription/${newSubscriptionId}`,
            );
          })
          .catch((error) => setErrorMessage(extractErrorMessage(error)));
      }
    },
  });

  useEffect(() => {
    if (subscriptionid) {
      getSubscription(membershipid, subscriptionid)
        .then((response) => {
          formik.setValues({
            subscriptionName: response.data.subscriptionName,
            startDate: response.data.startDate
              ? formatDateForInput(response.data.startDate)
              : '',
            endDate: response.data.endDate
              ? formatDateForInput(response.data.endDate)
              : '',
            status: response.data.status,
            paymentFrequency: response.data.paymentFrequency,
            nextPaymentDate: response.data.nextPaymentDate
              ? formatDateForInput(response.data.nextPaymentDate)
              : '',
            trial: response.data.trial,
            autoRenew: response.data.autoRenew,
            expired: response.data.expired,
            storageExceeded: response.data.storageExceeded,
            usersExceeded: response.data.usersExceeded,
            trialComplete: response.data.trialComplete,
            enabled: response.data.enabled,
          });
        })
        .catch((error) => setErrorMessage(extractErrorMessage(error)));
    }
  }, [subscriptionid]);

  const formatDateForInput = (dateString) => {
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 16);
  };

  // const cancelHandler = () => {
  //   navigate(`/system/dashboard/membership/${membershipid}`);
  // };

  return (
    <Container>
      <SystemDashboard>
        <Box marginTop={2} marginBottom={4}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link underline="hover" color="inherit" href="/">
              System Content
            </Link>
            <Link
              underline="hover"
              color="inherit"
              href="/system/dashboard/accounts"
            >
              Accounts
            </Link>
            <Link
              underline="hover"
              color="inherit"
              href="/system/dashboard/memberships"
            >
              Memberships
            </Link>
            <Link
              underline="hover"
              color="inherit"
              href="/system/dashboard/subscriptions"
            >
              Subscriptions
            </Link>
            <Typography color="text.primary">Subscription Detail</Typography>
          </Breadcrumbs>
        </Box>
        {errorMessage && (
          <Alert
            severity="error"
            onClose={() => setErrorMessage('')}
            sx={{ mb: 2 }}
          >
            {errorMessage}
          </Alert>
        )}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          marginBottom={4}
        >
          <Typography variant="h4">Subscription Detail</Typography>
        </Box>
        <Box>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={4}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Subscription Name"
                  name="subscriptionName"
                  fullWidth
                  value={formik.values.subscriptionName}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.subscriptionName &&
                    Boolean(formik.errors.subscriptionName)
                  }
                  helperText={
                    formik.touched.subscriptionName &&
                    formik.errors.subscriptionName
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Start Date"
                  type="datetime-local"
                  name="startDate"
                  fullWidth
                  value={formik.values.startDate}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.startDate && Boolean(formik.errors.startDate)
                  }
                  helperText={
                    formik.touched.startDate && formik.errors.startDate
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="End Date"
                  type="datetime-local"
                  name="endDate"
                  fullWidth
                  value={formik.values.endDate}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.endDate && Boolean(formik.errors.endDate)
                  }
                  helperText={formik.touched.endDate && formik.errors.endDate}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant={'subtitle2'}
                  sx={{ marginBottom: 2 }}
                  fontWeight={700}
                >
                  Is Trial?
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formik.values.trial} // Use Formik's value for 'trial'
                      onChange={formik.handleChange} // Let Formik handle the change
                      name="trial" // Name for the checkbox (bind it to Formik's field)
                    />
                  }
                  label="Is Trial"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography
                  variant={'subtitle2'}
                  sx={{ marginBottom: 2 }}
                  fontWeight={700}
                >
                  Auto Renew?
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formik.values.autoRenew} // Use Formik's value for 'autoRenew'
                      onChange={formik.handleChange} // Let Formik handle the change
                      name="autoRenew" // Name for the checkbox (bind it to Formik's field)
                    />
                  }
                  label="Auto Renew"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography
                  variant={'subtitle2'}
                  sx={{ marginBottom: 2 }}
                  fontWeight={700}
                >
                  Expired?
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formik.values.expired} // Use Formik's value for 'expired'
                      onChange={formik.handleChange} // Let Formik handle the change
                      name="expired" // Name for the checkbox (bind it to Formik's field)
                    />
                  }
                  label="Expired"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography
                  variant={'subtitle2'}
                  sx={{ marginBottom: 2 }}
                  fontWeight={700}
                >
                  Storage Exceeded?
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formik.values.storageExceeded} // Use Formik's value for 'storageExceeded'
                      onChange={formik.handleChange} // Let Formik handle the change
                      name="storageExceeded" // Name for the checkbox (bind it to Formik's field)
                    />
                  }
                  label="Storage Exceeded"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography
                  variant={'subtitle2'}
                  sx={{ marginBottom: 2 }}
                  fontWeight={700}
                >
                  Users Exceeded?
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formik.values.usersExceeded} // Use Formik's value for 'usersExceeded'
                      onChange={formik.handleChange} // Let Formik handle the change
                      name="usersExceeded" // Name for the checkbox (bind it to Formik's field)
                    />
                  }
                  label="Users Exceeded"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography
                  variant={'subtitle2'}
                  sx={{ marginBottom: 2 }}
                  fontWeight={700}
                >
                  Trial Complete?
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formik.values.trialComplete} // Use Formik's value for 'trialComplete'
                      onChange={formik.handleChange} // Let Formik handle the change
                      name="trialComplete" // Name for the checkbox (bind it to Formik's field)
                    />
                  }
                  label="Trial Complete"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography
                  variant={'subtitle2'}
                  sx={{ marginBottom: 2 }}
                  fontWeight={700}
                >
                  Enabled?
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formik.values.enabled} // Use Formik's value for 'enabled'
                      onChange={formik.handleChange} // Let Formik handle the change
                      name="enabled" // Name for the checkbox (bind it to Formik's field)
                    />
                  }
                  label="Enabled"
                />
              </Grid>
              <Grid item xs={12}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  marginBottom={4}
                >
                  <Button
                    variant="outlined"
                    onClick={() =>
                      navigate(`/system/dashboard/membership/${membershipid}`)
                    }
                  >
                    Cancel
                  </Button>
                  <Button type="submit" variant="contained">
                    Save
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Box>
        <Box>
          <SubscriptionProductList />
          <SubscriptionGroupList />
          <PaymentList />
        </Box>
      </SystemDashboard>
    </Container>
  );
};

export default SubscriptionDetail;
